import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Trophy, ShieldCheck } from "lucide-react";
import { ZONE_LABELS } from "../JerseyConfigurator";

/**
 * SponsorshipForm — right-side form on the ClubDetail page.
 * Handles zone summary, pricing breakdown, logo upload, company & message,
 * and POST /sponsorships submission.
 *
 * Props:
 *  - canSeePrices: bool — only validated sponsors / admins see prices and form
 *  - isPendingSponsor: bool — to show pending-status notice
 *  - user: current auth user (or null)
 *  - club: club object
 *  - age: current age category selected
 *  - selected: array of zone keys
 *  - prices: { zoneKey: price }
 *  - numPlayers, teamInfo: derived counts
 *  - logoPreview / setLogoPreview / setLogoFile: image preview state
 *  - logoPath / setLogoPath: uploaded image path
 */
export default function SponsorshipForm({
    canSeePrices,
    isPendingSponsor,
    user,
    club,
    age,
    selected,
    prices,
    numPlayers,
    teamInfo,
    logoPreview,
    setLogoFile,
    setLogoPreview,
    logoPath,
    setLogoPath,
}) {
    const nav = useNavigate();
    const [company, setCompany] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const perJersey = selected.reduce((s, k) => s + (prices[k] || 0), 0);
    const subtotal = perJersey * Math.max(1, numPlayers);
    const discountPct = numPlayers > 100 ? 30 : 0;
    const total = Math.round(subtotal * (100 - discountPct) / 100);

    const handleLogo = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!user) {
            toast.error("Connectez-vous pour uploader un logo");
            return;
        }
        setLogoFile(f);
        setLogoPreview(URL.createObjectURL(f));
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data: res } = await api.post("/uploads/logo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setLogoPath(res.path);
            toast.success("Logo uploadé");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || "Échec upload");
        }
    };

    const submit = async () => {
        if (!user) {
            toast.message("Connectez-vous pour sponsoriser");
            nav("/login");
            return;
        }
        if (user.role !== "sponsor") {
            toast.error("Seuls les sponsors peuvent réserver des emplacements");
            return;
        }
        if (!selected.length) {
            toast.error("Sélectionnez au moins un emplacement");
            return;
        }
        if (!logoPath) {
            toast.error("Uploadez votre logo");
            return;
        }
        if (!company.trim()) {
            toast.error("Renseignez le nom de l'entreprise");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/sponsorships", {
                club_id: club.id,
                age_category: age,
                zones: selected.map((name) => ({ name, price: prices[name] })),
                logo_path: logoPath,
                sponsor_company: company,
                message,
            });
            toast.success("Sponsoring créé ! Effectuez le virement pour finaliser.");
            nav(`/payment?sp=${data.id}`);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail) || "Échec");
        } finally {
            setSubmitting(false);
        }
    };

    if (!canSeePrices) {
        return <SponsorAccessNotice user={user} isPendingSponsor={isPendingSponsor} />;
    }

    return (
        <>
            <div className="card-tu">
                <h3 className="font-display text-2xl uppercase tracking-tight mb-4">
                    Votre sponsoring
                </h3>
                <div className="space-y-2 mb-4">
                    {selected.length === 0 ? (
                        <div className="text-sm text-zinc-500 italic">
                            Aucun emplacement sélectionné
                        </div>
                    ) : (
                        selected.map((k) => (
                            <div
                                key={k}
                                data-testid={`selected-zone-${k}`}
                                className="flex items-center justify-between border border-zinc-800 px-3 py-2 bg-zinc-950"
                            >
                                <span className="text-sm">{ZONE_LABELS[k]}</span>
                                <span className="font-bold text-orange-500">{prices[k]}€</span>
                            </div>
                        ))
                    )}
                </div>

                <div className="border-t border-zinc-800 pt-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-zinc-400">Prix par maillot</span>
                        <span className="font-bold text-zinc-100">{perJersey}€</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-zinc-400">Effectif {age}</span>
                        <span className="font-bold text-zinc-100" data-testid="num-players">
                            × {numPlayers || "—"} joueurs
                        </span>
                    </div>
                    {teamInfo.num_teams && (
                        <div className="text-[11px] text-zinc-500">
                            {teamInfo.num_teams} équipe{teamInfo.num_teams > 1 ? "s" : ""} de {teamInfo.players_per_team} joueurs
                        </div>
                    )}
                    {numPlayers > 100 ? (
                        <div className="flex items-center justify-between text-orange-500" data-testid="discount-line">
                            <span className="font-bold uppercase tracking-widest text-xs">
                                ✨ Remise fidélité 30%
                            </span>
                            <span className="font-bold">−{Math.round(perJersey * numPlayers * 0.3)}€</span>
                        </div>
                    ) : (
                        <div className="text-[11px] text-zinc-500">
                            💎 Plus de 100 maillots ? Remise auto de 30%.
                        </div>
                    )}
                    <div className="text-[11px] text-zinc-500 italic">
                        +{(numPlayers || 0) * 3} points fidélité Sponsormania.fr gagnés
                    </div>
                </div>
                <div className="flex items-center justify-between border-t border-zinc-800 pt-4 mt-4">
                    <span className="text-xs uppercase tracking-widest text-zinc-400">Total HT</span>
                    <span data-testid="sponsorship-total" className="font-display text-4xl text-orange-500">{total}€</span>
                </div>
            </div>

            <div className="card-tu">
                <label className="label-tu">Logo (PNG/SVG, max 8 Mo)</label>
                <label
                    className="block border-2 border-dashed border-zinc-700 hover:border-orange-500 p-6 text-center cursor-pointer transition-colors"
                    data-testid="logo-upload"
                >
                    <input
                        type="file"
                        accept="image/png,image/svg+xml,image/jpeg,image/webp"
                        onChange={handleLogo}
                        className="hidden"
                    />
                    {logoPreview ? (
                        <img src={logoPreview} alt="logo" className="max-h-32 mx-auto" />
                    ) : (
                        <div className="text-zinc-400">
                            <Upload className="mx-auto mb-2" size={28} />
                            <span className="text-sm">Glissez ou cliquez pour téléverser</span>
                        </div>
                    )}
                </label>

                <label className="label-tu mt-5">Entreprise / sponsor</label>
                <input
                    data-testid="sponsor-company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Nom de l'entreprise"
                    className="input-tu"
                />

                <label className="label-tu mt-5">Message au club (optionnel)</label>
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    placeholder="Bonjour, nous serions ravis…"
                    className="input-tu"
                />

                <button
                    data-testid="submit-sponsorship"
                    disabled={submitting}
                    onClick={submit}
                    className="btn-primary w-full mt-5"
                >
                    {submitting ? "Envoi…" : (
                        <>
                            <Trophy size={16} /> Sponsoriser pour {total}€
                        </>
                    )}
                </button>
                <p className="text-[11px] text-zinc-500 mt-3 text-center inline-flex items-center gap-1.5 justify-center w-full">
                    <ShieldCheck size={12} className="text-orange-500" /> 0% commission · sponsoring instantané
                </p>
            </div>
        </>
    );
}

function SponsorAccessNotice({ user, isPendingSponsor }) {
    return (
        <div className="card-tu border-l-4 border-l-orange-500">
            <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                Accès sponsor requis
            </div>
            {isPendingSponsor ? (
                <p className="text-zinc-300 text-sm">
                    Votre compte sponsor est <span className="font-bold">en attente de validation</span>.
                    Une fois approuvé par l'administration, vous pourrez consulter les tarifs et lancer
                    vos sponsorings.
                </p>
            ) : !user ? (
                <>
                    <p className="text-zinc-300 text-sm mb-4">
                        Connectez-vous en tant que sponsor validé pour découvrir les tarifs et sponsoriser ce club.
                    </p>
                    <div className="flex gap-2">
                        <Link to="/login" className="btn-primary py-2 px-4 text-xs">Se connecter</Link>
                        <Link to="/register" className="btn-ghost py-2 px-4 text-xs">Devenir sponsor</Link>
                    </div>
                </>
            ) : (
                <p className="text-zinc-300 text-sm">
                    Cette fonctionnalité est réservée aux sponsors validés.
                </p>
            )}
        </div>
    );
}
